import { FlagIcon } from "@heroicons/react/24/outline";
import { useMemo, useState } from "react";
import { LoadingSpinner } from "@/core/components/loading-spinner";
import { Button } from "@/core/components/ui/button";
import { LoadingCount } from "@/core/components/ui/loading-count/loading-count";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { IcmStatementSummaryCard } from "@/icm/icm-statement-page/statement-summary-card";
import { groupLineItemsByRule } from "@/icm/icm-statement-page/utils";
import type { IcmLineItem } from "@/icm/types";
import { PeriodSelector } from "./period-selector";
import { StatementLineItemCard } from "./statement-line-item-card";
import { TechnicianFeedbackSheet } from "./technician-feedback-sheet";
import { TechnicianLineItemSheet } from "./technician-line-item-sheet";
import type { TechnicianStatementExperienceProps } from "./types";

export const TechnicianStatementExperience = ({
  statement,
  isLoading,
  periodOptions,
  selectedPeriodStart,
  onPeriodChange,
  emptyTitle,
  emptyDescription,
}: TechnicianStatementExperienceProps) => {
  const [selectedLineItem, setSelectedLineItem] = useState<IcmLineItem | null>(
    null,
  );
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const lineItemsByRule = useMemo(
    () =>
      statement
        ? groupLineItemsByRule(
            (statement.line_items ?? []).filter((item) => item.job_id != null),
          )
        : {},
    [statement],
  );

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <>
      <ScrollArea className="h-[calc(100vh-66px)] w-full">
        <Stack
          fullWidth
          gap="lg"
          className="mx-auto max-w-2xl px-4 pb-24 pt-4 sm:px-6 sm:pb-24 sm:pt-6"
        >
          <PeriodSelector
            options={periodOptions}
            value={selectedPeriodStart}
            onValueChange={onPeriodChange}
          />

          {statement ? (
            <>
              <IcmStatementSummaryCard statement={statement} hideTotalPay />

              <Stack gap="xl" fullWidth>
                {Object.entries(lineItemsByRule).map(([ruleName, items]) => (
                  <Stack key={ruleName} gap="sm" fullWidth>
                    <Stack direction="row" align="center" gap="xs">
                      <Typography variant="subtitleW2">{ruleName}</Typography>
                      <LoadingCount count={items.length} variant="subtle" />
                    </Stack>

                    <div className="flex w-full flex-col gap-2">
                      {items.map((item) => (
                        <StatementLineItemCard
                          key={item.id}
                          lineItem={item}
                          onClick={() => setSelectedLineItem(item)}
                        />
                      ))}
                    </div>
                  </Stack>
                ))}
              </Stack>

              <div className="flex w-full justify-center">
                <Button
                  type="button"
                  variant="secondary"
                  leftIcon={<FlagIcon className="h-5 w-5" />}
                  onClick={() => setFeedbackOpen(true)}
                >
                  Dispute This Statement
                </Button>
              </div>
            </>
          ) : (
            <Stack
              gap="sm"
              align="center"
              justify="center"
              fullWidth
              className="py-16"
            >
              <Typography variant="subtitleW2" className="text-center">
                {emptyTitle}
              </Typography>
              <Typography
                variant="bodyW3"
                textColor="secondary"
                className="max-w-sm text-center"
              >
                {emptyDescription}
              </Typography>
            </Stack>
          )}
        </Stack>
      </ScrollArea>

      <TechnicianLineItemSheet
        lineItem={selectedLineItem}
        open={!!selectedLineItem}
        onOpenChange={(open) => !open && setSelectedLineItem(null)}
      />

      <TechnicianFeedbackSheet
        open={feedbackOpen}
        onOpenChange={setFeedbackOpen}
        statement={statement}
      />
    </>
  );
};
